import React, { useMemo, useRef, useState } from 'react';

import { showAlert } from '../components/general-components/alert-service';
import RtmpValidationOverride from '../components/modal/templates/custom/rtmp-validation-override';
import { isValidateRTMPEndpoint } from '../connection/broadcasting';
import { isValidRtmpAddress } from '../utils/utils';
import { GreenroomState as GS } from '../stores/types/types';
import { useSelector } from 'react-redux';

interface UseValidateRtmpReturn {
	isValidatingRtmpEndpoint: boolean;
	rtmpOverrideModal: JSX.Element;
	validateRtmpEndpoint: (
		editTitle: string,
		editAddress: string,
		editKey: string,
		offerSaveOnAttempt: number,		// Set this to 0 to never see the save prompt
		successCallback?: () => void,
	) => Promise<void>;
}

const useValidateRtmp = (): UseValidateRtmpReturn => {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isValidatingRtmpEndpoint, setIsValidatingRtmpEndpoint] = useState(false);
	const [validationAttempts, setValidationAttempts] = useState(0);
	const [previousAddressKey, setPreviousAddressKey] = useState('');

	const storedSuccessCallback = useRef<(() => void) | undefined>(undefined);

	const closeModal = (): void => {
		setIsModalOpen(false);
		setIsValidatingRtmpEndpoint(false);
		setValidationAttempts(0);
		setPreviousAddressKey('');
	};

	const resolveValidation = (): void => {
		storedSuccessCallback.current?.();
		closeModal();
	};

	const rtmpOverrideModal = useMemo((): JSX.Element => (
		isModalOpen
			? (
				<RtmpValidationOverride
					resolveValidation={resolveValidation}
					closeModal={closeModal}
				/>
			)
			: <></>
	), [isModalOpen]);

	const rtmpFormIsValid = (
		editTitle: string,
		editAddress: string,
		editKey: string,
	): boolean => {
		// If the user has not entered a title
		if (!editTitle) {
			showAlert({
				type: 'scheduled-stream-schedule-error',
				message: 'Please give this destination a name',
				duration: 5000,
				autoWidth: true,
			});

			return false;
		}

		// If the user has not entered an RTMP address or stream key
		if (!editAddress || !editKey) {
			const headerArray = ['No'];
			const bodyArray = ['Please enter'];

			if (!editAddress) headerArray.push('RTMP Address');
			if (!editAddress && !editKey) headerArray.push('or');
			if (!editKey) headerArray.push('Stream Key');

			if (!editAddress) bodyArray.push('an RTMP address');
			if (!editAddress && !editKey) bodyArray.push('and');
			if (!editKey) bodyArray.push('a stream key');

			showAlert({
				type: 'scheduled-stream-schedule-error',
				message: headerArray.join(' '),
				description: bodyArray.join(' '),
				duration: 5000,
				autoWidth: true,
			});

			return false;
		}

		const rtmpAddressIsMalformed = editAddress && !isValidRtmpAddress(editAddress);

		// If the user has entered an RTMP address that is malformed
		if (rtmpAddressIsMalformed) {
			showAlert({
				type: 'scheduled-stream-schedule-error',
				message: 'RTMP format incorrect',
				description: 'Please use the format rtmp(s)://<address>',
				duration: 5000,
				autoWidth: true,
			});

			return false;
		}

		return true;
	};

	const validateRtmpEndpoint = async (
		editTitle: string,
		editAddress: string,
		editKey: string,
		offerSaveOnAttempt: number,
		successCallback?: () => void,
	): Promise<void> => {
		const isFormValid = rtmpFormIsValid(
			editTitle,
			editAddress,
			editKey,
		);

		if (isValidatingRtmpEndpoint || !isFormValid) {
			return;
		}

		const addressKey = `${editAddress}${editKey}`;

		let attempts = validationAttempts + 1;

		if (previousAddressKey && previousAddressKey !== addressKey) {
			attempts = 1;
		}

		storedSuccessCallback.current = successCallback;
		setValidationAttempts(attempts);
		setPreviousAddressKey(addressKey);

		// If user has failed validation `offerSaveOnAttempt` times,
		// offer them the opportunity to save anyway.
		if (offerSaveOnAttempt && attempts >= offerSaveOnAttempt) {
			setIsModalOpen(true);

			return;
		}

		setIsValidatingRtmpEndpoint(true);

		const result = await isValidateRTMPEndpoint(token ?? '', editAddress, editKey);

		setIsValidatingRtmpEndpoint(false);

		if (result) {
			resolveValidation();

			return;
		}

		showAlert({
			type: 'scheduled-stream-schedule-error',
			message: 'Invalid RTMP Destination',
			description: 'Please check for spaces or errors',
			duration: 5000,
			autoWidth: true,
		});
	};

	return {
		isValidatingRtmpEndpoint,
		rtmpOverrideModal,
		validateRtmpEndpoint,
	};
};

export default useValidateRtmp;
